import { useCallback } from 'react';
import { Button, IconPlus, Stack, Text } from '@adc/parallax-component-library';
import { createFileRoute, Outlet } from '@tanstack/react-router';

import type { IDN, IDNSort, SortDirection } from '~/api/types';
import { IDNTable } from '~/components/table/idn/IDNTable';
import type { IDNTableColumnKey } from '~/components/table/idn/types';
import { USER_ROLES } from '~/features/auth/roles';
import { useModal } from '~/features/modals/useModal';
import type { IDNSearchParams } from '~/types/IDNRoute';
import { buildTestId } from '~/utils/buildTestId';
import { protectedRoute } from '~/utils/routesAuthorization';

const TEST_ID = 'IDN-table';

export const Route = createFileRoute('/customer-administration/idns')({
  component: RouteComponent,
  validateSearch: (search): IDNSearchParams => {
    // Validate and parse the search params into a typed state
    const defaultSortDir: SortDirection =
      search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort: (search.sort as IDNSort | undefined) ?? undefined,
      search:
        search.search && typeof search.search === 'string'
          ? decodeURI(search.search)
          : undefined,
      sortDir,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.AdminAbbott, USER_ROLES.AbbottUser],
    });
  },
});

function RouteComponent() {
  const { page = 1, search = '', sort, sortDir = 'desc' } = Route.useSearch();

  const navigate = Route.useNavigate();

  const { openModal } = useModal();

  const handleSearch = async (searchValue: string) => {
    // Update the search term and reset to the first page
    await navigate({
      search: (prev) => ({ ...prev, page: 1, search: encodeURI(searchValue) }),
    });
  };

  const handlePageChange = async (newPage: number) => {
    // Update the page number in the search params
    await navigate({
      search: (prev) => ({ ...prev, page: newPage }),
    });
  };

  const handleSort = async (columnKey: IDNTableColumnKey) => {
    // Update the sort column and direction in the search params
    await navigate({
      search: (prev) => ({
        ...prev,
        sort: columnKey,
        sortDir: sort === columnKey && sortDir === 'asc' ? 'desc' : 'asc',
      }),
    });
  };

  const onRowPress = useCallback(
    async (idn: IDN) => {
      await navigate({
        to: '/customer-administration/idns/$idnId',
        params: { idnId: idn.id },
      });
    },
    [navigate],
  );

  const handleViewIDNProfile = useCallback(
    (idnId: string) => {
      void navigate({
        to: '/customer-administration/idns/$idnId',
        params: { idnId },
      });
    },
    [navigate],
  );

  return (
    <Stack height="auto" testID={TEST_ID}>
      <Text
        type="$h3.medium"
        marginBottom="$2"
        testID={buildTestId({ testID: TEST_ID, suffix: 'title' })}
      >
        IDN Administration
      </Text>
      <IDNTable
        testID={TEST_ID}
        page={page}
        search={search}
        sort={sort}
        sortDir={sortDir}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        onSortChange={handleSort}
        onRowPress={onRowPress}
        headerActions={
          <Button
            testID={buildTestId({
              testID: TEST_ID,
              suffix: ['add', 'department', 'button'],
            })}
            size="medium"
            variant="primary"
            label="Add IDN"
            iconStart={<IconPlus />}
            onPress={() => {
              openModal('IDNForm', {
                testID: 'idn-create-modal',
                handleViewIDNProfile,
              });
            }}
          />
        }
      />
      <Outlet />
    </Stack>
  );
}
